import React, {
  useState,
  useCallback,
  Fragment,
  FC,
} from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import styles from './ListaItem.scss';
import { getElementID, getMergedClassNames } from 'utils/HTMLUtils';
import { removePeriodoByIdAndPeriodo } from 'store/modules/EscolasActions';
import { openModal } from 'store/modules/ModalActions';
import SnackbarUtils from 'utils/SnackbarUtils';
import { RootState } from 'store';
import AccordionArrowOpenClose from 'assets/icons/AccordionArrowOpenClose';
import MoreVertIcon from 'components/Icons/MoreVertIcon';
import ModalAviso from '../../../../components/Modais/ModalAviso';
import { useHabilitaCampoPrograma } from '../../../../hooks/useDisablePrograma';
import { TipoPrograma } from '../../../../__enum';
import { ListaItemProps } from '../../../../__types';
import { useModalPeriodoPrograma } from '../../../../hooks/useModalProgramas';

const rootClassName = styles.rootClassName;

const ListaItem: FC<ListaItemProps> = ({ id, periodo, desabilitarDuplicar, onRefresh }) => {
  const { classeCondicional, isDesabilitado } = useHabilitaCampoPrograma(TipoPrograma.UniaoFazAVida);
  const [anchorEl, setAnchorEl] = useState(null);
  const [opened, setOpened] = useState(false);
  const { openModalFormDados } = useModalPeriodoPrograma();
  const languageValues = useSelector((state: RootState) => (state.languages.languageValues));
  const dispatch = useDispatch();

  const toggleOpen = useCallback(() => setOpened((prev) => !prev), []);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const labels = useCallback(
    () => [
      null,
      languageValues.educacaoInfantilLabel,
      languageValues.ensinoFundamentalILabel,
      languageValues.ensinoFundamentalIILabel,
      languageValues.ensinoMedioLabel,
      languageValues.ejaLabel,
      languageValues.classeEspecializadaLabel,
    ],
    [languageValues]
  );

  const valueTypes = useCallback(
    () => [
      null,
      'edInfantil',
      'ensFundI',
      'ensFundII',
      'ensMedio',
      'eja',
      'especializada',
      'colaboradores'
    ],
    []
  );

  const getTotalByType = useCallback(() => {
    const valueType = valueTypes();
    let totalProf = 0;
    let totalAlun = 0;
    let totalColab = 0;

    valueType.forEach((type, index) => {
      if (index === 0) return;
      if (type === 'colaboradores') {
        totalColab += periodo[type] ?? 0;
      } else {
        totalProf += periodo[`${type}Prof`] ?? 0;
        totalAlun += periodo[`${type}Alun`] ?? 0;
      }
    });
    return {
      professores: totalProf,
      alunos: totalAlun,
      colaboradores: totalColab,
    };
  }, [periodo, valueTypes]);

  const totais = getTotalByType();

  const toEdit = useCallback(() => {
    openModalFormDados({
      key: "editar",
      tipoPrograma: TipoPrograma.UniaoFazAVida,
      id: periodo.idEscola,
      periodo: periodo.periodo,
      idPeriodo: periodo.idPeriodo,
      onRefresh,
    });
  }, [openModalFormDados, periodo, onRefresh]);

  const duplicar = useCallback(() => {
    openModalFormDados({
      key: "duplicar",
      tipoPrograma: TipoPrograma.UniaoFazAVida,
      id: periodo.idEscola,
      periodo: periodo.periodo,
      idPeriodo: periodo.idPeriodo,
      onRefresh,
    });
  }, [openModalFormDados, periodo, onRefresh]);

  const removing = useCallback(() => {
    dispatch(
      removePeriodoByIdAndPeriodo(
        periodo.idEscola,
        periodo.periodo,
        () => {
          SnackbarUtils.enqueueSnackbar({
            message: languageValues.successRemovePeriodo,
            options: { variant: 'success' },
          });
        },
        (error: any) => {
          SnackbarUtils.enqueueSnackbar({
            message: error.response?.statusText || languageValues.errorRemovePeriodo,
            options: { variant: 'error' },
          });
        }
      )
    );
  }, [dispatch, periodo, languageValues]);

  const toRemove = useCallback(() => {
    dispatch(
      openModal(ModalAviso, {
        modalKey: 'modal-remover-programa',
        title: languageValues.removerDadosDaEscolaTitle,
        cancelCallback: () => '',
        confirmCallback: () => { removing() },
        confirmLabel: languageValues.removerDadosDaEscolaModalConfirmLabel,
        cancelLabel: 'cancelar',
        content: (
          <Fragment>
            {languageValues.removerDadosDaEscolaModalDescription
              .split('${1}')
              .join(periodo.periodo.split('-').join('/'))}
          </Fragment>
        ),
        confirmThemeButton: 'button_PE',
        cancelThemeButton: 'button_PE',
        tipoPrograma: TipoPrograma.UniaoFazAVida,
        ano: periodo.ano,
        idEscola: periodo.idEscola,
      })
    );
  }, [dispatch,
    languageValues.removerDadosDaEscolaModalConfirmLabel,
    languageValues.removerDadosDaEscolaModalDescription,
    languageValues.removerDadosDaEscolaTitle,
    periodo.ano,
    periodo.idEscola,
    periodo.periodo,
    removing]);

  const allowChange = periodo.permiteEdicao;
  const allowDelete = periodo.permiteExclusao;

  return (
    <Fragment>
      <TableRow
        id={getElementID(id, 'row')}
        className={`${classeCondicional} ${rootClassName}-periodo-row`}
      >
        <TableCell className={getMergedClassNames([
          `${classeCondicional}`,
          `${rootClassName}-cell`,
          `${rootClassName}-action-buttons-cell`,
        ])}>
          {periodo.periodo}
        </TableCell>
        <TableCell className={getMergedClassNames([
          `${classeCondicional}`,
          `${rootClassName}-cell`,
          `${rootClassName}-action-buttons-cell`,
        ])}>
          {totais.colaboradores}
        </TableCell>
        <TableCell className={getMergedClassNames([
          `${classeCondicional}`,
          `${rootClassName}-cell`,
          `${rootClassName}-action-buttons-cell`,
        ])}>
          {totais.professores}
        </TableCell>
        <TableCell className={getMergedClassNames([
          `${classeCondicional}`,
          `${rootClassName}-cell`,
          `${rootClassName}-action-buttons-cell`,
        ])}>
          {totais.alunos}
        </TableCell>
        <TableCell className={getMergedClassNames([
          `${rootClassName}-cell`,
          `${rootClassName}-action-buttons-cell`,
        ])}>
          <div>
            <IconButton onClick={(e) => {
              e.stopPropagation();
              handleMenuClick(e);
            }}>
              <MoreVertIcon />
            </IconButton>

            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                toggleOpen();
              }}
              aria-expanded={opened}
            >
              <AccordionArrowOpenClose
                className={`accordion-icon ${opened ? 'opened' : ''}`}
                style={{ fill: '#33820D' }}
              />
            </IconButton>

          </div>
        </TableCell>

        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
        >
          <MenuItem className='menu-item' disabled={!allowChange || isDesabilitado} onClick={() => {
            toEdit()
            handleMenuClose();
          }}>
            Editar
          </MenuItem>
          <MenuItem className='menu-item' disabled={desabilitarDuplicar || isDesabilitado} onClick={() => {
            duplicar()
            handleMenuClose();
          }}>
            Duplicar
          </MenuItem>
          <MenuItem className='menu-item' disabled={!allowDelete || isDesabilitado} onClick={() => {
            toRemove()
            handleMenuClose();
          }}>
            Excluir
          </MenuItem>
        </Menu>
      </TableRow>

      {opened && valueTypes().map((type, index) => {
        if (index === 0) return null;

        const isLast = index >= valueTypes().length - 1;
        const noBorderClass = isLast ? '' : `${rootClassName}-no-border`;

        const labelCellClass = `${classeCondicional} ${rootClassName}-cell ${rootClassName}-label-cell ${noBorderClass}`;
        const valueCellClass = `${classeCondicional} ${rootClassName}-cell ${noBorderClass}`;
        const emptyCellClass = `${rootClassName}-cell ${noBorderClass}`;

        return (
          <TableRow className={`${rootClassName}-periodo-sub-row`} key={index}>
            <TableCell className={labelCellClass} colSpan={2}>
              {labels()[index]}
            </TableCell>
            <TableCell className={valueCellClass}>
              {periodo[`${type}Prof`]}
            </TableCell>
            <TableCell className={valueCellClass}>
              {periodo[`${type}Alun`]}
            </TableCell>
            <TableCell className={emptyCellClass} />
            <TableCell className={emptyCellClass} />
            <TableCell className={`${rootClassName}-cell ${rootClassName}-no-border`} />
          </TableRow>
        );
      })}
    </Fragment>
  )
};

export default ListaItem;
